import * as React from 'react';
import s from './PageFooter.scss';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { withInjectedRootStore, RootStore } from 'providers';
import { Text } from '@wix/design-system';
import { Experiments, MediaDevices, Theme } from 'enums';
import { useBreakpoint } from 'providers/BreakpointProvider';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';

export interface MoneyBackGuaranteeProps extends InjectedExperimentsProps {
  rootStore: RootStore;
}

const titleKeyByDevice = {
  [MediaDevices.Desktop]: 'page_footer.money_back_guarantee',
  [MediaDevices.Tablet]: 'page_footer.money_back_guarantee.non_desktop',
  [MediaDevices.Mobile]: 'page_footer.money_back_guarantee.non_desktop',
};

const secondUpgradeTitleKeyByDevice = {
  [MediaDevices.Desktop]: 'page_footer.money_back_guarantee_second_upgrade',
  [MediaDevices.Tablet]:
    'page_footer.money_back_guarantee_second_upgrade.non_desktop',
  [MediaDevices.Mobile]:
    'page_footer.money_back_guarantee_second_upgrade.non_desktop',
};

const commonContent = {
  iconClass: s.fourteenDaysIcon,
  titleKey: titleKeyByDevice,
  descKey: 'page_footer.try_14_days',
};

const secondUpgradeCommonContent = {
  iconClass: s.creditCardIcon,
  titleKey: secondUpgradeTitleKeyByDevice,
  descKey: 'app.second_upgrade_text',
};

const themedContent = {
  [Theme.AdvancedPlans]: commonContent,
  [Theme.WixStudio]: {
    ...commonContent,
    descKey: 'studio_package_picker.footer.try_wix_studio_free',
  },
  [Theme.EditorX]: {
    ...commonContent,
    descKey: 'page_footer.try_14_days_editorx',
  },
  [Theme.Dayful]: {
    ...commonContent,
    descKey: 'page_footer.try_14_days_dayful',
  },
};

const MoneyBackGuaranteeComp: React.FunctionComponent<
  MoneyBackGuaranteeProps
> = ({ rootStore, experiments }) => {
  const {
    isSecondUpgrade,
    isSepaCountry,
    appContext: {
      pageMode: { theme },
    },
  } = rootStore.appStore;

  const shouldShowSepaDisclaimer =
    isSepaCountry && !experiments.enabled(Experiments.MoneyBackGuaranteeInSepa);

  const sepaDisclaimer = {
    ...themedContent[theme],
    ...{ descKey: 'page_footer.try_14_days_sepa' },
  };

  const elms = isSecondUpgrade
    ? secondUpgradeCommonContent
    : shouldShowSepaDisclaimer
    ? sepaDisclaimer
    : themedContent[theme];

  const { t } = useTranslation();
  const { currentBreakpoint } = useBreakpoint();

  return (
    <div className={s.singleElementWrapper}>
      <div
        className={classNames(s.singleElement, s.middle)}
        data-testid='money-back'
      >
        <div className={s.sideIconContainer}>
          <i className={elms.iconClass} />
          <p>
            <Text weight='bold' size='tiny' className={s.title}>
              {t(elms.titleKey[currentBreakpoint ?? MediaDevices.Desktop])}
            </Text>
            <Text
              className={s.desc}
              weight='thin'
              size='tiny'
              data-testid={`money-back-${theme}`}
            >
              {t(elms.descKey)}
            </Text>
          </p>
        </div>
      </div>
    </div>
  );
};

export const MoneyBackGuarantee = withInjectedRootStore(
  withExperiments(MoneyBackGuaranteeComp),
);
